import { router } from "@inertiajs/react"
import {
  BookOpen,
  Layers,
  List,
  Clock,
  Plus,
  Search,
} from "lucide-react"
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import type {
  PaginationMeta,
  QualityMetrics,
  StudioGuideItem,
  StudioPoiRow,
  StudioUrls,
} from "@/lib/types"

import { GuideItemRow } from "./GuideItemRow"
import { PreviewPanel } from "./PreviewPanel"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type GroupBy = "flat" | "time" | "category"

export interface GuideTabHandle {
  save: () => void
  dirty: boolean
  saving: boolean
}

interface GuideTabProps {
  guide_items: StudioGuideItem[]
  pois: { records: StudioPoiRow[]; meta: PaginationMeta }
  urls: StudioUrls
  quality: QualityMetrics
  regionCenter: [number, number] | null
  onDirtyChange?: (dirty: boolean) => void
  onSavingChange?: (saving: boolean) => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TIME_SECTION_ORDER = ["morning", "afternoon", "evening", "all_day", "untagged"] as const
const TIME_SECTION_LABELS: Record<string, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  all_day: "All day",
  untagged: "Untagged",
}

function groupByTime(items: StudioGuideItem[]) {
  const groups: Record<string, StudioGuideItem[]> = {}
  for (const section of TIME_SECTION_ORDER) {
    groups[section] = []
  }
  for (const item of items) {
    const key = item.time_context ?? "untagged"
    if (!groups[key]) groups[key] = []
    groups[key].push(item)
  }
  return TIME_SECTION_ORDER
    .filter((k) => groups[k].length > 0)
    .map((k) => ({ key: k, label: TIME_SECTION_LABELS[k], items: groups[k] }))
}

function groupByCategory(items: StudioGuideItem[]) {
  const groups: Record<string, StudioGuideItem[]> = {}
  const order: string[] = []

  for (const item of items) {
    const cat = item.poi?.categories?.[0]?.name ?? "Uncategorized"
    if (!groups[cat]) {
      groups[cat] = []
      order.push(cat)
    }
    groups[cat].push(item)
  }

  return order.map((key) => ({ key, label: key, items: groups[key] }))
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GuideTab = forwardRef<GuideTabHandle, GuideTabProps>(
  function GuideTab({ guide_items, pois, urls, quality, regionCenter, onDirtyChange, onSavingChange }, ref) {
    const [items, setItems] = useState<StudioGuideItem[]>(guide_items)
    const [dirty, setDirtyState] = useState(false)
    const [saving, setSavingState] = useState(false)
    const [groupBy, setGroupBy] = useState<GroupBy>("flat")
    const [libraryOpen, setLibraryOpen] = useState(false)
    const [librarySearch, setLibrarySearch] = useState("")
    const [selectedItemId, setSelectedItemId] = useState<number | null>(null)

    function setDirty(value: boolean) {
      setDirtyState(value)
      onDirtyChange?.(value)
    }

    function setSaving(value: boolean) {
      setSavingState(value)
      onSavingChange?.(value)
    }

    // -----------------------------------------------------------------------
    // Save handler
    // -----------------------------------------------------------------------

    const saveGuide = useCallback(() => {
      setSaving(true)
      router.patch(
        urls.update_guide,
        {
          items: items
            .filter((i) => i.poi)
            .map((item, idx) => ({
              poi_id: item.poi!.id,
              position: idx,
              host_note: item.host_note,
              is_highlighted: item.is_highlighted,
              time_context: item.time_context,
            })),
        },
        {
          preserveScroll: true,
          onFinish: () => {
            setSaving(false)
            setDirty(false)
          },
        },
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, urls.update_guide])

    // Expose save to parent via ref
    useImperativeHandle(
      ref,
      () => ({ save: saveGuide, dirty, saving }),
      [saveGuide, dirty, saving],
    )

    // -----------------------------------------------------------------------
    // Item mutations
    // -----------------------------------------------------------------------

    function markDirty() {
      setDirty(true)
    }

    function moveUp(index: number) {
      if (index === 0) return
      setItems((prev) => {
        const next = [...prev]
        ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
        return next
      })
      markDirty()
    }

    function moveDown(index: number) {
      setItems((prev) => {
        if (index >= prev.length - 1) return prev
        const next = [...prev]
        ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
        return next
      })
      markDirty()
    }

    function removeItem(index: number) {
      setItems((prev) => prev.filter((_, i) => i !== index))
      markDirty()
    }

    function updateItem(index: number, patch: Partial<StudioGuideItem>) {
      setItems((prev) => {
        const next = [...prev]
        next[index] = { ...next[index], ...patch }
        return next
      })
      markDirty()
    }

    function addFromLibrary(poi: StudioPoiRow) {
      const newItem: StudioGuideItem = {
        id: -Date.now(), // temp negative ID for new items
        position: items.length + 1,
        host_note: null,
        is_highlighted: false,
        time_context: null,
        poi: {
          id: poi.id,
          name: poi.name,
          categories: poi.categories,
          cover_url: poi.cover_url,
          lat: poi.lat,
          lng: poi.lng,
        },
        event: null,
      }
      setItems((prev) => [...prev, newItem])
      markDirty()
    }

    // -----------------------------------------------------------------------
    // Library drawer filter
    // -----------------------------------------------------------------------

    const guidePoiIds = useMemo(
      () => new Set(items.filter((i) => i.poi).map((i) => i.poi!.id)),
      [items],
    )

    const libraryPois = useMemo(() => {
      const available = pois.records.filter(
        (p) => p.status === "approved" && !guidePoiIds.has(p.id),
      )
      if (!librarySearch.trim()) return available
      const q = librarySearch.toLowerCase()
      return available.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.categories.some((c) => c.name.toLowerCase().includes(q)),
      )
    }, [pois.records, guidePoiIds, librarySearch])

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------

    function renderItemRow(item: StudioGuideItem, index: number) {
      return (
        <GuideItemRow
          key={item.poi?.id ?? item.id}
          item={item}
          index={index}
          isFirst={index === 0}
          isLast={index === items.length - 1}
          onMoveUp={() => moveUp(index)}
          onMoveDown={() => moveDown(index)}
          onRemove={() => removeItem(index)}
          onChange={(patch) => updateItem(index, patch)}
        />
      )
    }

    function renderGrouped(
      sections: { key: string; label: string; items: StudioGuideItem[] }[],
    ) {
      // We need absolute indices for move up/down to work across sections
      let absoluteIndex = 0
      return sections.map((section) => (
        <div key={section.key}>
          <div className="border-b bg-muted/40 px-4 py-1.5 text-xs font-medium text-muted-foreground">
            {section.label}{" "}
            <span className="text-muted-foreground/60">({section.items.length})</span>
          </div>
          <ul className="divide-y">
            {section.items.map((item) => {
              const idx = absoluteIndex++
              return renderItemRow(item, idx)
            })}
          </ul>
        </div>
      ))
    }

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    // Empty state
    if (items.length === 0 && !libraryOpen) {
      return (
        <div className="flex lg:gap-6">
          <div className="flex-1">
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
              <BookOpen className="mb-3 size-10 text-muted-foreground/40" />
              <h3 className="text-sm font-medium">Your guide is empty</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Add POIs from the library to start building your regional guide
              </p>
              <Button
                size="sm"
                className="mt-4"
                onClick={() => setLibraryOpen(true)}
              >
                <Plus className="mr-1.5 size-4" />
                Add from library
              </Button>
            </div>
          </div>
          {/* Preview panel (desktop) */}
          <div className="hidden w-[440px] shrink-0 lg:block">
            <PreviewPanel
              items={items}
              quality={quality}
              selectedItemId={selectedItemId}
              onSelectItem={setSelectedItemId}
              regionCenter={regionCenter}
            />
          </div>
        </div>
      )
    }

    return (
      <div className="flex gap-6">
        {/* Left side: item list */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">
              Guide order{" "}
              <span className="font-medium text-foreground">
                {items.length}
              </span>{" "}
              {items.length === 1 ? "item" : "items"}
            </span>

            {/* Group-by segmented control */}
            <div className="flex rounded-md border bg-muted/40 p-0.5">
              <Button
                variant={groupBy === "flat" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 gap-1.5 px-2.5 text-xs"
                onClick={() => setGroupBy("flat")}
              >
                <List className="size-3.5" />
                Flat
              </Button>
              <Button
                variant={groupBy === "time" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 gap-1.5 px-2.5 text-xs"
                onClick={() => setGroupBy("time")}
              >
                <Clock className="size-3.5" />
                Time
              </Button>
              <Button
                variant={groupBy === "category" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 gap-1.5 px-2.5 text-xs"
                onClick={() => setGroupBy("category")}
              >
                <Layers className="size-3.5" />
                Category
              </Button>
            </div>

            <div className="flex-1" />

            <Button
              variant={libraryOpen ? "secondary" : "outline"}
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => setLibraryOpen((prev) => !prev)}
            >
              <Plus className="size-3.5" />
              Add from library
            </Button>
          </div>

          {/* Library drawer */}
          {libraryOpen && (
            <Card className="border-dashed">
              <CardContent className="space-y-2 py-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                  <Input
                    placeholder="Search approved POIs..."
                    value={librarySearch}
                    onChange={(e) => setLibrarySearch(e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
                <div className="max-h-[240px] space-y-0.5 overflow-y-auto">
                  {libraryPois.length === 0 ? (
                    <p className="py-4 text-center text-xs text-muted-foreground">
                      {pois.records.filter((p) => p.status === "approved").length === guidePoiIds.size
                        ? "All approved POIs are already in the guide"
                        : "No matching POIs found"}
                    </p>
                  ) : (
                    libraryPois.map((poi) => (
                      <div
                        key={poi.id}
                        className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm">
                          {poi.name}
                        </span>
                        {poi.categories.slice(0, 2).map((c) => (
                          <Badge
                            key={c.id}
                            variant="outline"
                            className="shrink-0 text-[10px]"
                          >
                            {c.name}
                          </Badge>
                        ))}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 shrink-0"
                          onClick={() => addFromLibrary(poi)}
                          aria-label={`Add ${poi.name} to guide`}
                        >
                          <Plus className="size-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Item list */}
          <Card>
            <div>
              {groupBy === "flat" && (
                <ul className="divide-y">
                  {items.map((item, index) => renderItemRow(item, index))}
                </ul>
              )}
              {groupBy === "time" && renderGrouped(groupByTime(items))}
              {groupBy === "category" && renderGrouped(groupByCategory(items))}
            </div>
          </Card>
        </div>

        {/* Right side: preview panel (desktop) */}
        <div className="hidden w-[440px] shrink-0 lg:block">
          <PreviewPanel
            items={items}
            quality={quality}
            selectedItemId={selectedItemId}
            onSelectItem={setSelectedItemId}
            regionCenter={regionCenter}
          />
        </div>
      </div>
    )
  },
)
